import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';
import { Shield, Mail, KeyRound } from 'lucide-react';
import { tempGuestAPI } from '../../services/api';

const MODULE_LABELS = {
  ropa_intake: 'ROPA Intake',
  breach_intake: 'Breach Intake',
  dpia_intake: 'DPIA Intake',
  prescreen: 'Pre-Screen Assessment',
  readiness_assessment: 'Readiness Assessment',
  questionnaire: 'Questionnaire',
};

// Step 1: email entry — determines OTP vs password
// Step 2a (OTP): show code input
// Step 2b (password): show password input

export default function GuestLoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'otp' | 'password'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [authMethod, setAuthMethod] = useState(null); // 'otp' | 'password'
  const [loading, setLoading] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const probe = await tempGuestAPI.probeAuthMethod(email.trim().toLowerCase());
      const method = probe.data.auth_method;
      setAuthMethod(method);
      if (method === 'otp') {
        await tempGuestAPI.requestOtp(email.trim().toLowerCase());
        setStep('otp');
        toast.success('Check your inbox — a 6-digit code has been sent.');
      } else {
        setStep('password');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setLoading(true);
    try {
      const res = await tempGuestAPI.verifyOtp(email.trim().toLowerCase(), code.trim());
      _handleLoginSuccess(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Invalid or expired code.';
      if (detail.includes('expired')) {
        toast.error('Your access has expired. Please contact your DPO.');
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const res = await tempGuestAPI.passwordLogin(email.trim().toLowerCase(), password);
      _handleLoginSuccess(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Invalid credentials.';
      if (detail.includes('expired')) {
        toast.error('Your access has expired. Please contact your DPO.');
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const _handleLoginSuccess = (data) => {
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('guest_mode', 'true');
    localStorage.setItem('guest_user', JSON.stringify(data.user));
    navigate('/guest/portal');
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Branding */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/20 mb-2">
            <Shield className="h-6 w-6 text-violet-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Assura</h1>
          <p className="text-sm text-slate-400">Secure guest access</p>
        </div>

        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-4">
            <CardTitle className="text-white text-lg">
              {step === 'email' && 'Sign in'}
              {step === 'otp' && 'Enter your code'}
              {step === 'password' && 'Enter your password'}
            </CardTitle>
            <CardDescription className="text-slate-400">
              {step === 'email' && 'Enter the email address you were invited with.'}
              {step === 'otp' && `We sent a 6-digit code to ${email}`}
              {step === 'password' && `Signing in as ${email}`}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {step === 'email' && (
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-9 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                      required
                      autoFocus
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-700 text-white" disabled={loading}>
                  {loading ? 'Please wait...' : 'Continue'}
                </Button>
              </form>
            )}

            {step === 'otp' && (
              <form onSubmit={handleOtpSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code" className="text-slate-300">6-digit code</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="code"
                      type="text"
                      inputMode="numeric"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="pl-9 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 tracking-widest text-center text-lg font-mono"
                      maxLength={6}
                      autoFocus
                    />
                  </div>
                  <p className="text-xs text-slate-500">Code expires in 10 minutes. If you don't receive it, your access may have been revoked — contact your DPO.</p>
                </div>
                <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-700 text-white" disabled={loading || code.length !== 6}>
                  {loading ? 'Verifying...' : 'Sign in'}
                </Button>
                <div className="flex items-center justify-between text-sm">
                  <button type="button" onClick={handleEmailSubmit} className="text-slate-400 hover:text-slate-300 underline-offset-2 hover:underline" disabled={loading}>
                    Resend code
                  </button>
                  <button type="button" onClick={() => setStep('password')} className="text-slate-400 hover:text-slate-300 underline-offset-2 hover:underline">
                    Use password instead
                  </button>
                </div>
              </form>
            )}

            {step === 'password' && (
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your temporary password"
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    required
                    autoFocus
                  />
                </div>
                <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-700 text-white" disabled={loading}>
                  {loading ? 'Signing in...' : 'Sign in'}
                </Button>
                <button
                  type="button"
                  onClick={() => { setStep('email'); setPassword(''); }}
                  className="w-full text-sm text-slate-400 hover:text-slate-300 underline-offset-2 hover:underline"
                >
                  ← Back
                </button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-600">
          This is a secure, time-limited session. For help, contact the person who invited you.
        </p>
      </div>
    </div>
  );
}
